import type { SoundKey } from "../audio/types";

/**
 * "Follow the melody" — musical Simon. Pure reducer: the UI plays the
 * sequence and forwards the child's input; only the state lives here.
 *
 * Didactics: auditory and working memory, sequencing. Tolerant to
 * failure (2-6 years): on a miss the SAME sequence repeats, there are no lives.
 */

export interface SimonConfig {
  /** Subset of keys over which sequences are generated. */
  keys: readonly SoundKey[];
  /** Initial sequence length. */
  startLength: number;
  /** Maximum length (end of a victorious game). */
  maxLength: number;
  /** Injectable source of randomness (deterministic tests). */
  random?: () => number;
}

export type SimonPhase = "demo" | "input" | "won";

export interface SimonState {
  sequence: SoundKey[];
  /** Position of the child's input within the sequence. */
  inputIndex: number;
  phase: SimonPhase;
  /** Current level (1 = sequence of startLength). */
  level: number;
}

export interface SimonGame {
  state: () => SimonState;
  /** The UI finished playing the demo: child's turn. */
  demoFinished: () => void;
  /**
   * Child's input. Returns the effect for the UI:
   * - "progress": partial hit, continue
   * - "level-up": sequence completed, a longer demo comes
   * - "retry": miss, the demo of the same sequence repeats
   * - "won": completed the maximum length
   * - "ignored": not input turn
   */
  handleInput: (key: SoundKey) => "progress" | "level-up" | "retry" | "won" | "ignored";
}

export function createSimonGame(config: SimonConfig): SimonGame {
  const random = config.random ?? Math.random;
  const pick = (): SoundKey => {
    const index = Math.floor(random() * config.keys.length);
    return config.keys[index] ?? config.keys[0] ?? "C4";
  };

  const state: SimonState = {
    sequence: Array.from({ length: config.startLength }, pick),
    inputIndex: 0,
    phase: "demo",
    level: 1,
  };

  return {
    state: () => ({ ...state, sequence: [...state.sequence] }),
    demoFinished: () => {
      if (state.phase === "demo") {
        state.phase = "input";
        state.inputIndex = 0;
      }
    },
    handleInput: (key) => {
      if (state.phase !== "input") {
        return "ignored";
      }
      if (key !== state.sequence[state.inputIndex]) {
        // Miss: repeats the same sequence from the demo.
        state.phase = "demo";
        state.inputIndex = 0;
        return "retry";
      }
      state.inputIndex += 1;
      if (state.inputIndex < state.sequence.length) {
        return "progress";
      }
      if (state.sequence.length >= config.maxLength) {
        state.phase = "won";
        return "won";
      }
      state.sequence.push(pick());
      state.level += 1;
      state.phase = "demo";
      state.inputIndex = 0;
      return "level-up";
    },
  };
}
